import { useQuery } from '@tanstack/react-query';
import { request } from './client';

/**
 * What is left of the M4 operations surfaces: the SLA reply clocks that the
 * Messages screen reads a due date from, and the PHI-Match access check. The
 * SLA board, coverage and leave screen was retired on 2026-10-09 (its API
 * routes remain); the REDCap import and the weekly extract live in `redcap.ts`.
 *
 * None of this is PHI on its own. A ticket names a clock and an owner. The
 * participant's name is joined in only for callers who could already read the
 * submission, and pack 03 is explicit that the participant never sees the SLA
 * numbers.
 */

export type ClockState = 'none' | 'ok' | 'at-risk' | 'breached';

export interface Ticket {
  id: string;
  kind: string;
  kindLabel: string;
  subjectType: string;
  subjectId: string;
  subjectName: string | null;
  subjectStatus: string | null;
  ownerId: string | null;
  ownerName: string | null;
  dueAt: string | null;
  clock: ClockState;
  status: string;
  statusLabel: string;
  note: string | null;
  createdAt: string;
  /** The subject is PHI and this role may not see who it is. */
  redacted: boolean;
}

const qs = (params: Record<string, string | undefined>): string => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) s.set(k, v);
  const out = s.toString();
  return out ? `?${out}` : '';
};

/**
 * The open reply clock on each submission, by submission id — where the
 * Messages screen reads a due date from.
 *
 * The earliest deadline wins when a submission has two. Clocks on hold are
 * left out: a held clock is not running. The list is capped at 100 a request
 * and sorted soonest first, so it is paged, up to 500, which is more open
 * clocks than the lab has ever held at once.
 */
export const useReplyClocks = () =>
  useQuery({
    queryKey: ['tickets', 'reply-clocks'],
    queryFn: async () => {
      const bySubmission = new Map<string, Ticket>();
      for (let offset = 0; offset < 500; offset += 100) {
        const page = await request<{ total: number; tickets: Ticket[] }>(
          `/tickets${qs({ limit: '100', offset: String(offset) })}`,
        );
        for (const t of page.tickets) {
          if (t.subjectType !== 'submission' || !t.dueAt || t.status === 'hold') continue;
          const held = bySubmission.get(t.subjectId);
          if (!held?.dueAt || held.dueAt > t.dueAt) bySubmission.set(t.subjectId, t);
        }
        if (offset + 100 >= page.total) break;
      }
      return bySubmission;
    },
    staleTime: 60_000,
  });

/**
 * Every running reply clock, counted (GET /tickets/summary, analytics:read).
 * `breached` is past its deadline and `atRisk` due within four hours, held
 * clocks included; `open` is every clock not on hold. Typed here because the
 * route declares no response schema.
 */
export interface ClockSummary {
  open: number;
  atRisk: number;
  breached: number;
  onHold: number;
  unassigned: number;
}

/** `enabled` lets a role without analytics:read skip the request instead of collecting a 403. */
export const useClockSummary = (enabled: boolean) =>
  useQuery({
    queryKey: ['tickets', 'summary'],
    queryFn: () => request<ClockSummary>('/tickets/summary'),
    enabled,
    staleTime: 60_000,
  });

/** What PHI-Match access this session holds, so the UI can hide what it must. */
export const usePhiMatchAccess = () =>
  useQuery({
    queryKey: ['phi-match-access'],
    queryFn: () =>
      request<{
        level: 'none' | 'view' | 'match';
        label: string;
        blurb: string;
        canView: boolean;
        canMatch: boolean;
      }>('/matching/access'),
    staleTime: 60_000,
  });
